import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import {
  AlertCommunicationService,
  AlertData
} from '../shared/alert-communication.service';

export interface Alert {
  id?: string;
  type: string;
  title: string;
  message: string;
  dismissable: boolean;
}

@Component({
  selector: 'app-working-alerts',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="alerts-wrapper" *ngIf="alerts.length > 0">
      <div
        *ngFor="let alert of alerts; trackBy: trackByFn"
        class="alert"
        [ngClass]="[
          'alert-' + alert.type,
          alert.id && fadingAlerts.has(alert.id) ? 'fade-out' : ''
        ]"
      >
        <div class="alert-content">
          <div class="alert-header" *ngIf="alert.title">
            <strong>{{ alert.title }}</strong>
          </div>
          <div class="alert-message">
            {{ alert.message }}
          </div>
        </div>
        <button
          *ngIf="alert.dismissable"
          class="alert-dismiss"
          (click)="dismissAlert(alert)"
          aria-label="Close alert"
        >
          ×
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .alerts-wrapper {
        position: fixed;
        top: 80px;
        right: 20px;
        z-index: 9999;
        max-width: 400px;
        width: 100%;
        pointer-events: none;
      }

      .alert {
        pointer-events: auto;
        display: flex;
        align-items: flex-start;
        margin-bottom: 12px;
        padding: 16px;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        background: white;
        border-left: 4px solid;
        animation: slideInRight 0.3s ease-out;
        position: relative;
        min-height: 60px;
        transition: opacity 0.5s ease-out, transform 0.5s ease-out;
      }

      .alert.fade-out {
        opacity: 0;
        transform: translateX(100%);
      }

      @keyframes slideInRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      .alert-success {
        border-left-color: #28a745;
        background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
      }

      .alert-danger {
        border-left-color: #dc3545;
        background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
      }

      .alert-info {
        border-left-color: #17a2b8;
        background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
      }

      .alert-warning {
        border-left-color: #ffc107;
        background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
      }

      .alert-content {
        flex: 1;
        margin-right: 10px;
      }

      .alert-header {
        font-weight: 600;
        margin-bottom: 4px;
        color: #333;
        font-size: 14px;
      }

      .alert-message {
        color: #555;
        font-size: 13px;
        line-height: 1.4;
      }

      .alert-dismiss {
        background: none;
        border: none;
        font-size: 20px;
        line-height: 1;
        color: #666;
        cursor: pointer;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.2s ease;
      }

      .alert-dismiss:hover {
        background: rgba(0, 0, 0, 0.1);
        color: #333;
      }

      @media (max-width: 768px) {
        .alerts-wrapper {
          top: 60px;
          right: 10px;
          left: 10px;
          max-width: none;
        }

        .alert {
          padding: 12px;
          font-size: 12px;
        }
      }
    `
  ]
})
export class WorkingAlertsComponent implements OnInit, OnDestroy {
  alerts: Alert[] = [];
  private subscription?: Subscription;
  private alertTimeouts: Map<string, number> = new Map();
  fadingAlerts: Set<string> = new Set();

  constructor(
    private cdr: ChangeDetectorRef,
    private alertCommunicationService: AlertCommunicationService
  ) {}

  ngOnInit() {
    // Subscribe to the communication service for reliable alert delivery
    this.subscription = this.alertCommunicationService
      .getAlertsObservable()
      .subscribe((newAlert: AlertData) => {
        this.addAlertToDisplay(newAlert);
      });
  }

  private addAlertToDisplay(alertData: AlertData) {
    // Check if alert already exists
    if (!this.alerts.find(a => a.id === alertData.id)) {
      const alert: Alert = {
        id: alertData.id,
        type: alertData.type,
        title: alertData.title,
        message: alertData.message,
        dismissable: alertData.dismissable
      };

      this.alerts.unshift(alert); // Add to beginning for newest first
      this.cdr.detectChanges();

      // Auto-dismiss the alert after 1.5 seconds with fade-out animation
      if (alert.id) {
        const timeoutId = window.setTimeout(() => {
          this.startFadeOut(alert);
        }, 1500);
        this.alertTimeouts.set(alert.id, timeoutId);
      }
    }
  }

  private startFadeOut(alert: Alert) {
    if (alert.id) {
      this.fadingAlerts.add(alert.id);
      this.cdr.detectChanges();

      // Remove the alert after the fade-out animation completes (0.5s)
      setTimeout(() => {
        this.dismissAlert(alert);
      }, 500);
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    // Clear all pending timeouts
    this.alertTimeouts.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    this.alertTimeouts.clear();
    this.fadingAlerts.clear();
  }

  dismissAlert(alert: Alert) {
    const index = this.alerts.findIndex(a => a.id === alert.id);
    if (index > -1) {
      this.alerts.splice(index, 1);
      this.cdr.detectChanges();

      // Clear the timeout if it exists
      if (alert.id && this.alertTimeouts.has(alert.id)) {
        clearTimeout(this.alertTimeouts.get(alert.id));
        this.alertTimeouts.delete(alert.id);
      }

      // Remove from fading alerts set
      if (alert.id) {
        this.fadingAlerts.delete(alert.id);
      }
    }
  }

  trackByFn(index: number, alert: Alert) {
    return alert.id || index;
  }
}
